/**
 * Admin widget: active impersonation sessions + recent 24h audit timeline.
 * Polls every 30s so multiple admins can see each other's sessions live.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, Clock, ShieldAlert, ZapOff } from "lucide-react";
import { API } from "../../config";

function fmtAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function ImpersonationAuditWidget() {
  const [data, setData] = useState({ active: [], recent: [], active_count: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [killing, setKilling] = useState(null);

  const load = () => {
    axios.get(`${API}/admin-users/impersonation-log?hours=24&limit=30`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  const forceStop = async (jti, target) => {
    if (!jti) return;
    if (!window.confirm(`Force-stop impersonation of ${target}?\nAll future requests with this token will fail.`)) return;
    setKilling(jti);
    try {
      await axios.post(`${API}/admin-users/impersonation-log/force-stop`, { jti }, { withCredentials: true });
      load();
    } catch (err) {
      alert(`Force-stop failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setKilling(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded p-4 mb-4" data-testid="impersonation-audit-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-bold text-gray-800">Impersonation Audit (last 24h)</h3>
        </div>
        <div className="flex items-center gap-2">
          {data.active_count > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-800 rounded" data-testid="imp-active-count">
              {data.active_count} ACTIVE
            </span>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-gray-500 hover:text-gray-800"
            data-testid="imp-widget-toggle"
          >
            {expanded ? "Hide" : "View history"}
          </button>
        </div>
      </div>

      {loading && <p className="text-xs text-gray-400">Loading…</p>}

      {!loading && data.active_count === 0 && !expanded && (
        <p className="text-xs text-gray-400 italic">No active impersonation sessions.</p>
      )}

      {data.active.length > 0 && (
        <div className="space-y-1.5 mb-2" data-testid="imp-active-list">
          {data.active.map((s, i) => (
            <div key={i} className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
              <Eye className="w-3 h-3 text-amber-600 flex-shrink-0" />
              <span className="font-semibold text-amber-900">{s.admin_email}</span>
              <span className="text-gray-500">→</span>
              <span className="font-semibold text-gray-700">{s.target_email}</span>
              <span className="text-gray-400 ml-auto flex items-center gap-1">
                <Clock className="w-3 h-3" /> {fmtAgo(s.started_at)}
              </span>
              {s.jti && (
                <button
                  onClick={() => forceStop(s.jti, s.target_email)}
                  disabled={killing === s.jti}
                  className="ml-1 px-2 py-0.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-[10px] font-bold rounded flex items-center gap-1 transition-colors"
                  data-testid={`imp-force-stop-${i}`}
                  title="Immediately revoke this session's token"
                >
                  <ZapOff className="w-3 h-3" />
                  {killing === s.jti ? "…" : "Force stop"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {expanded && data.recent.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 max-h-60 overflow-y-auto space-y-1" data-testid="imp-recent-list">
          {data.recent.map((r, i) => {
            const isStart = r.action === "start";
            const isForce = r.action === "force_stop";
            const ts = r.started_at || r.stopped_at;
            return (
              <div key={i} className="flex items-center gap-2 text-[11px] text-gray-600 py-0.5">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isStart ? 'bg-amber-100 text-amber-700' : isForce ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                  {isStart ? "START" : isForce ? "FORCE" : "STOP"}
                </span>
                <span className="truncate max-w-[150px]">{r.admin_email || "—"}</span>
                <span className="text-gray-300">→</span>
                <span className="truncate max-w-[150px]">{r.target_email || "—"}</span>
                <span className="text-gray-400 ml-auto">{fmtAgo(ts)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
